import React, { useEffect, useMemo, useState } from 'react';
import DriveBucketBadge from '../primitives/DriveBucketBadge';
import { RiPagination } from '../primitives/States';
import { TONE_STYLES } from '../../shared/MetricCardShell';
import { cleanLocationLabel } from '../lib/labelFormat';

const fmt1 = (n) => (n == null ? '—' : Number(n).toFixed(1));

/** Markets shown per page in the drawer's origin list. */
export const MARKETS_PAGE_SIZE = 10;

// Collapse origins that resolve to the same city/state label. The route_intelligence
// table sometimes carries multiple geocoded points for the same market (e.g. two
// Camarillo, CA rows with coords ~5 km apart), which surfaced as visual duplicates
// per markup-comment #47. We merge them by their cleaned human-readable label,
// average the metrics, and surface the original count so users can still tell
// when a "market" is actually backed by N distinct origin points.
function groupOriginsByMarket(origins) {
  const groups = new Map();
  for (const o of origins) {
    const label = o.origin_label
      ? cleanLocationLabel(o.origin_label)
      : (o.origin_lat != null && o.origin_lon != null
          ? `${o.origin_lat.toFixed(2)}, ${o.origin_lon.toFixed(2)}`
          : '—');
    const key = label.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, {
        key, label,
        origins: [],
        bucketCounts: {},
        driveSum: 0, driveN: 0,
        milesSum: 0, milesN: 0,
        fuelSum: 0, fuelN: 0,
        hasBooking: false,
      });
    }
    const g = groups.get(key);
    g.origins.push(o);
    if (o.drive_bucket) g.bucketCounts[o.drive_bucket] = (g.bucketCounts[o.drive_bucket] || 0) + 1;
    if (o.drive_time_hours != null) { g.driveSum += Number(o.drive_time_hours); g.driveN++; }
    if (o.drive_distance_miles != null) { g.milesSum += Number(o.drive_distance_miles); g.milesN++; }
    if (o.fuel_gallons != null) { g.fuelSum += Number(o.fuel_gallons); g.fuelN++; }
    if (o.has_booking) g.hasBooking = true;
  }
  return [...groups.values()].map((g) => {
    const topBucket = Object.entries(g.bucketCounts).sort((a, b) => b[1] - a[1])[0]?.[0]
      || g.origins[0]?.drive_bucket;
    return {
      key: g.key,
      label: g.label,
      count: g.origins.length,
      representative: g.origins[0],
      drive_bucket: topBucket,
      drive_time_hours: g.driveN ? Number((g.driveSum / g.driveN).toFixed(1)) : null,
      drive_distance_miles: g.milesN ? Math.round(g.milesSum / g.milesN) : null,
      fuel_gallons: g.fuelN ? Number((g.fuelSum / g.fuelN).toFixed(1)) : null,
      has_booking: g.hasBooking,
    };
  });
}

/**
 * A compact stat: the page's neutral border and background, with the value in a
 * colour from the same TONE_STYLES as `MetricCardShell` (no tint, no coloured
 * outline, no watermark icon at this size). The tone is decorative: it tells the six stats
 * apart and is never set from the value.
 */
function Stat({ label, value, tone = 'brandBlue' }) {
  const style = TONE_STYLES[tone] ?? TONE_STYLES.brandBlue;
  return (
    <div data-tone={tone} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-1 text-lg font-extrabold leading-tight ${style.valueClass}`}>{value}</div>
    </div>
  );
}

export default function StateDetailPanel({ stateName, stateRow, origins, onClose, onSelectOrigin }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const markets = useMemo(() => groupOriginsByMarket(origins), [origins]);

  // Long states (Georgia: 98 markets) made the drawer scroll for pages — show 10 at a
  // time. Back to page 1 whenever the state or its (filtered) origin list changes.
  const [marketPage, setMarketPage] = useState(1);
  useEffect(() => { setMarketPage(1); }, [stateName, markets]);
  const marketPages = Math.max(1, Math.ceil(markets.length / MARKETS_PAGE_SIZE));
  const pagedMarkets = useMemo(
    () => markets.slice((marketPage - 1) * MARKETS_PAGE_SIZE, marketPage * MARKETS_PAGE_SIZE),
    [markets, marketPage],
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <aside
        role="dialog"
        aria-label={`${stateName} origins`}
        className="fixed top-0 right-0 h-full w-[480px] max-w-full bg-white dark:bg-gray-900 shadow-xl z-50 overflow-y-auto border-l border-gray-200 dark:border-gray-800"
      >
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">State</div>
            <h2 className="text-lg font-semibold text-[#0B6FB0] dark:text-[#32B1FF]">{stateName}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none p-1">✕</button>
        </div>

        <div className="p-5 space-y-5">
          {stateRow && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Stat tone="brandBlue" label="Origins" value={stateRow.total_origins?.toLocaleString() ?? '—'} />
              <Stat tone="amber" label="Avg drive (hrs)" value={stateRow.avg_drive_hours ?? '—'} />
              <Stat tone="purple" label="Avg miles" value={stateRow.avg_miles ?? '—'} />
              <Stat tone="emerald" label="Avg fuel (gal)" value={stateRow.avg_fuel_gallons ?? '—'} />
              <Stat tone="brandBlue" label="Avg congestion (min)" value={stateRow.avg_congestion_min ?? '—'} />
              <Stat
                tone="purple"
                label="Drive range (hrs)"
                value={stateRow.min_hours != null && stateRow.max_hours != null
                  ? `${stateRow.min_hours} – ${stateRow.max_hours}`
                  : '—'}
              />
            </div>
          )}

          <div>
            {/* Section band — the same tint as the table header bands on this page. */}
            <div data-testid="state-origins-heading" className="mb-2 rounded-md bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {origins.length.toLocaleString()} {origins.length === 1 ? 'origin' : 'origins'} in this state
              {markets.length < origins.length && (
                <span> · {markets.length.toLocaleString()} unique markets</span>
              )}
            </div>
            {markets.length === 0 ? (
              <div className="text-sm italic text-gray-500 dark:text-gray-400">
                No matching origins for the active filters.
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-800 -mx-5">
                {pagedMarkets.map((m) => (
                  <li key={m.key}>
                    <button
                      type="button"
                      onClick={() => onSelectOrigin(m.representative)}
                      className="w-full text-left px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset"
                    >
                      <div className="flex items-start gap-3">
                        <DriveBucketBadge bucket={m.drive_bucket} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {m.label}
                            {m.count > 1 && (
                              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                · {m.count} origins
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {m.drive_time_hours != null ? `${fmt1(m.drive_time_hours)} hrs` : '—'}
                            {m.drive_distance_miles != null ? ` · ${Math.round(Number(m.drive_distance_miles)).toLocaleString()} mi` : ''}
                            {m.fuel_gallons != null ? ` · ${fmt1(m.fuel_gallons)} gal` : ''}
                            {m.has_booking && <span className="ml-2 text-emerald-600 dark:text-emerald-400">• booked</span>}
                            {m.count > 1 && (
                              <span className="ml-2 text-gray-400 dark:text-gray-500">(avg)</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <RiPagination
              page={marketPage}
              totalPages={marketPages}
              onPageChange={setMarketPage}
              totalItems={markets.length}
              itemLabel="market"
            />
          </div>
        </div>
      </aside>
    </>
  );
}
